import * as React from 'react'
import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'
import { validateFormInput } from '@/shared/lib/accessibility-validator'

export interface InputProps extends React.ComponentProps<'input'> {
  /** Label text to display above the input */
  label?: string
  /** Error message to display below the input */
  error?: string
  /** Optional helper text to display below the input when no error */
  helperText?: string
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, style, label, error, helperText, id, ...props }, ref) => {
    // Generate unique ID for accessibility if not provided
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    const errorId = `${inputId}-error`
    const helperId = `${inputId}-helper`

    // Development-only validation (validateFormInput internally checks isDev())
    const hasLabel = !!(label || props['aria-label'] || props['aria-labelledby'])
    validateFormInput(hasLabel, type || 'text', 'Input')

    // If no label prop is provided, return simple input (backward compatibility)
    if (!label && !error && !helperText) {
      return (
        <input
          id={inputId}
          type={type}
          ref={ref}
          style={{ ...styles.input, ...style }}
          className={cn('input', className)}
          {...props}
        />
      )
    }

    // Full enhanced input with label and error support
    return (
      <div style={styles.container}>
        {label && (
          <label htmlFor={inputId} style={styles.label}>
            {label}
            {props.required && <span style={styles.required}> *</span>}
          </label>
        )}
        <input
          id={inputId}
          type={type}
          ref={ref}
          style={{
            ...styles.input,
            ...(error ? styles.inputError : {}),
            ...style,
          }}
          className={cn('input', className)}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            error ? errorId : helperText ? helperId : undefined
          }
          {...props}
        />
        {error && (
          <span id={errorId} role="alert" style={styles.error}>
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={helperId} style={styles.helperText}>
            {helperText}
          </span>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    lineHeight: 1.2,
  },
  required: {
    color: 'var(--error)',
  },
  input: {
    display: 'flex',
    height: '36px',
    width: '100%',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '4px 12px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all var(--transition-fast)',
    outline: 'none',
  },
  inputError: {
    borderColor: 'var(--error)',
    background: 'var(--error-bg-faint)',
  },
  error: {
    fontSize: '12px',
    color: 'var(--error)',
    lineHeight: 1.3,
  },
  helperText: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.3,
  },
}
